import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowDown, ArrowLeft, ArrowUp, Plus } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { Chip } from '../../components/ui/Chip'
import { Input } from '../../components/ui/Input'
import { ApiError, apiFetch, apiFetchRaw } from '../../lib/api-client'
import { formatOere } from '../../lib/money'
import {
  s, apiMsg, cardStyle, thStyle, tdStyle, fieldLabel, fieldControl, fmtDate,
  type Applicant, type Listing,
} from './listings-shared'
import { ErrorLine, ListingStatusChip, PageHeader } from './ListingParts'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const APPLICANT_VARIANT: Record<string, 'gray' | 'blue' | 'green' | 'red'> = {
  ny: 'blue',
  venteliste: 'gray',
  accepteret: 'green',
  afvist: 'red',
}

export function ListingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [listing, setListing] = React.useState<Listing | null>(null)
  const [applicants, setApplicants] = React.useState<Applicant[]>([])
  const [applicantsForbidden, setApplicantsForbidden] = React.useState(false)
  const [portalStatus, setPortalStatus] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [notFound, setNotFound] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  const [showAdd, setShowAdd] = React.useState(false)
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [addError, setAddError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    if (!id) return
    try {
      setListing(await apiFetch<Listing>(`/api/v1/listings/${id}`))
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true)
      else setError(s.loadError)
      setLoading(false)
      return
    }
    try {
      setApplicants(await apiFetch<Applicant[]>(`/api/v1/listings/${id}/applicants`))
      setApplicantsForbidden(false)
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setApplicantsForbidden(true)
      else setError(s.loadError)
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  React.useEffect(() => {
    apiFetch<{ status: string }>('/api/v1/listings/portals')
      .then((p) => setPortalStatus(p.status))
      .catch(() => setPortalStatus(null))
  }, [])

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(apiMsg(e, s.actionError))
      void load()
    } finally {
      setBusy(false)
    }
  }

  const publish = () =>
    run(async () => setListing(await apiFetch<Listing>(`/api/v1/listings/${id}/publish`, { method: 'POST' })))

  const closeListing = () => {
    if (!window.confirm(s.closeConfirm)) return Promise.resolve()
    return run(async () => setListing(await apiFetch<Listing>(`/api/v1/listings/${id}/close`, { method: 'POST' })))
  }

  // The backend takes the target queue position: the neighbour's current position.
  const move = (a: Applicant, target: number | null) =>
    run(async () => {
      if (target === null) return
      const positions = await apiFetch<{ id: string; queue_position: number }[]>(
        `/api/v1/listings/applicants/${a.id}/move`,
        { method: 'POST', body: JSON.stringify({ position: target }) },
      )
      const pos = new Map(positions.map((p) => [p.id, p.queue_position]))
      setApplicants((rs) =>
        rs
          .map((r) => ({ ...r, queue_position: pos.get(r.id) ?? r.queue_position }))
          .sort((x, y) => (x.queue_position ?? 0) - (y.queue_position ?? 0)),
      )
    })

  const setStatus = (a: Applicant, status: string) =>
    run(async () => {
      const updated = await apiFetch<Applicant>(`/api/v1/listings/applicants/${a.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      setApplicants((rs) => rs.map((r) => (r.id === updated.id ? updated : r)))
    })

  const remove = (a: Applicant) => {
    if (!window.confirm(s.removeConfirm)) return Promise.resolve()
    return run(async () => {
      // DELETE answers 204 with no body, which apiFetch cannot parse.
      const res = await apiFetchRaw(`/api/v1/listings/applicants/${a.id}`, { method: 'DELETE' })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: unknown; error?: unknown }
        const d = body.detail ?? body.error
        throw new ApiError(res.status, typeof d === 'string' && d ? d : 'Request failed')
      }
      await load()
    })
  }

  const contractDraft = (a: Applicant) =>
    run(async () => {
      const res = await apiFetch<{ contract_path: string }>(
        `/api/v1/listings/applicants/${a.id}/contract-draft`,
        { method: 'POST' },
      )
      navigate(res.contract_path)
    })

  async function addApplicant(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return setAddError(s.errApplicantName)
    if (!EMAIL_RE.test(email.trim())) return setAddError(s.errApplicantEmail)
    setAddError(null)
    setBusy(true)
    try {
      await apiFetch<Applicant>(`/api/v1/listings/${id}/applicants`, {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          notes: notes.trim() || null,
        }),
      })
      setName(''); setEmail(''); setPhone(''); setNotes('')
      setShowAdd(false)
      await load()
    } catch (err) {
      setAddError(apiMsg(err, s.addError))
    } finally {
      setBusy(false)
    }
  }

  const back = (
    <Link to="/annoncering" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', textDecoration: 'none', marginBottom: 16 }}>
      <ArrowLeft size={14} />
      {s.back}
    </Link>
  )

  if (loading) {
    return <AppLayout><div style={{ fontSize: 14, color: '#9ca3af' }}>{s.loading}</div></AppLayout>
  }
  if (notFound || !listing) {
    return (
      <AppLayout>
        {back}
        <div style={{ fontSize: 14, color: notFound ? '#6b7280' : '#dc2626' }}>{notFound ? s.notFound : error ?? s.loadError}</div>
      </AppLayout>
    )
  }

  const isClosed = listing.status === 'closed'

  return (
    <AppLayout>
      {back}
      <PageHeader
        title={listing.title}
        subtitle={`${formatOere(listing.husleje_oere)} pr. måned`}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <ListingStatusChip status={listing.status} />
            {listing.status === 'draft' && (
              <Button onClick={() => void publish()} disabled={busy}>{s.publish}</Button>
            )}
            {!isClosed && (
              <Button variant="secondary" onClick={() => void closeListing()} disabled={busy}>{s.close}</Button>
            )}
          </div>
        }
      />
      {error && <ErrorLine>{error}</ErrorLine>}

      <div style={{ ...cardStyle, padding: 20, marginBottom: 20 }}>
        {listing.description ? (
          <p style={{ margin: 0, fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {listing.description}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13, color: '#6b7280', marginTop: listing.description ? 12 : 0 }}>
          <span>{s.publishedAt}: {fmtDate(listing.published_at)}</span>
          <span>{s.closedAt}: {fmtDate(listing.closed_at)}</span>
        </div>
      </div>

      <section
        aria-disabled="true"
        style={{ ...cardStyle, padding: 20, marginBottom: 20, background: '#f8fafc', borderStyle: 'dashed', opacity: 0.85 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>{s.portalsTitle}</span>
          <Chip variant="gray">{portalStatus === 'kommer' || portalStatus === null ? 'Kommer' : portalStatus}</Chip>
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#94a3b8' }}>{s.portalsBody}</p>
      </section>

      {applicantsForbidden ? (
        <div role="status" style={{ ...cardStyle, padding: 24, fontSize: 14, color: '#6b7280' }}>{s.forbidden}</div>
      ) : (<>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>{s.applicantsTitle}</h2>
        {!showAdd && (
          <Button size="sm" variant="secondary" onClick={() => setShowAdd(true)} disabled={isClosed} title={isClosed ? s.closedNoAdd : undefined}>
            <Plus size={14} />
            {s.addApplicant}
          </Button>
        )}
      </div>

      {showAdd && (
        <form onSubmit={(e) => void addApplicant(e)} style={{ ...cardStyle, padding: 20, marginBottom: 16, maxWidth: 640 }}>
          <div style={{ display: 'grid', gap: 14 }}>
            <Input id="applicant-name" label={s.applicantName} value={name} onChange={(e) => setName(e.target.value)} />
            <Input id="applicant-email" type="email" label={s.applicantEmail} value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input id="applicant-phone" label={s.applicantPhone} value={phone} onChange={(e) => setPhone(e.target.value)} />
            <div>
              <label htmlFor="applicant-notes" style={fieldLabel}>{s.applicantNotes}</label>
              <textarea
                id="applicant-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                style={{ ...fieldControl, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
              />
            </div>
          </div>
          {addError && <ErrorLine>{addError}</ErrorLine>}
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <Button type="submit" isLoading={busy}>{s.addApplicant}</Button>
            <Button type="button" variant="secondary" onClick={() => { setShowAdd(false); setAddError(null) }}>{s.cancel}</Button>
          </div>
        </form>
      )}

      <div style={{ ...cardStyle, overflow: 'hidden' }}>
        {applicants.length === 0 ? (
          <div style={{ padding: 32, fontSize: 14, color: '#9ca3af' }}>{s.applicantsEmpty}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e8eaed' }}>
                  <th style={thStyle}>{s.colPos}</th>
                  <th style={thStyle}>{s.colName}</th>
                  <th style={thStyle}>{s.colEmail}</th>
                  <th style={thStyle}>{s.colPhone}</th>
                  <th style={thStyle}>{s.colApplicantStatus}</th>
                  <th style={thStyle}>{s.colActions}</th>
                </tr>
              </thead>
              <tbody>
                {applicants.map((a, i) => (
                  <tr key={a.id} data-testid="applicant-row" style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{a.queue_position ?? i + 1}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: '#111827', minWidth: 140 }}>
                      {a.name}
                      {a.notes && <div style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginTop: 2 }}>{a.notes}</div>}
                    </td>
                    <td style={{ ...tdStyle, minWidth: 180 }}>{a.email}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{a.phone ?? '—'}</td>
                    <td style={tdStyle}>
                      <Chip variant={APPLICANT_VARIANT[a.status] ?? 'gray'}>{s.applicantStatus[a.status] ?? a.status}</Chip>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button size="sm" variant="ghost" aria-label={`${s.moveUp} ${a.name}`} title={s.moveUp}
                          disabled={busy || isClosed || i === 0} onClick={() => void move(a, applicants[i - 1]?.queue_position ?? null)}>
                          <ArrowUp size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" aria-label={`${s.moveDown} ${a.name}`} title={s.moveDown}
                          disabled={busy || isClosed || i === applicants.length - 1} onClick={() => void move(a, applicants[i + 1]?.queue_position ?? null)}>
                          <ArrowDown size={14} />
                        </Button>
                        <Button size="sm" variant="secondary" aria-label={`${s.accept} ${a.name}`}
                          disabled={busy || isClosed || a.status === 'accepteret'} onClick={() => void setStatus(a, 'accepteret')}>
                          {s.accept}
                        </Button>
                        <Button size="sm" variant="secondary" aria-label={`${s.reject} ${a.name}`}
                          disabled={busy || isClosed || a.status === 'afvist'} onClick={() => void setStatus(a, 'afvist')}>
                          {s.reject}
                        </Button>
                        {a.status === 'accepteret' && (
                          <Button size="sm" aria-label={`${s.contractDraft} ${a.name}`} disabled={busy} onClick={() => void contractDraft(a)}>
                            {s.contractDraft}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" aria-label={`${s.remove} ${a.name}`}
                          style={{ color: '#dc2626' }} disabled={busy} onClick={() => void remove(a)}>
                          {s.remove}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}
    </AppLayout>
  )
}
